/**
 * How the rest of the header asks the ⌘K search dialog to open.
 *
 * `NavSearchDialog` owns its own open state and is rendered beside the nav
 * dropdowns rather than above them, so there is no shared parent to lift that
 * state into. A window event keeps the two decoupled: a caller needs no ref and
 * no context, and the dialog remains the only thing that knows how it opens.
 */

import { NAV_MENU_LABEL_SECTIONS } from "@/lib/navigation/nav-manifest";

export const NAV_SEARCH_OPEN_EVENT = "salesably:nav-search-open";

/**
 * A header menu's label and the name the search index files its pages under are
 * not always the same word: the header says "My Results" where the index says
 * "Results", and "Team Reports" where it says "Reports". Both come from the one
 * manifest entry, so a rename cannot leave them disagreeing.
 *
 * An unknown label falls through to itself, matches no section, and the
 * tiebreak simply stops firing — the failure mode is "no preference", never a
 * wrong preference.
 */
export function resolveNavSection(menuLabel) {
  if (!menuLabel) return "";
  return NAV_MENU_LABEL_SECTIONS[menuLabel] || menuLabel;
}

/**
 * Open the global search dialog.
 *
 * @param {object} [options]
 * @param {string} [options.query] Text to seed the input with — typically the
 *   keystroke that triggered the handoff, so no character is lost.
 * @param {boolean} [options.pagesFirst] Rank page links above content results.
 *   Set when the surface the user was looking at was itself a list of pages.
 * @param {string} [options.menuLabel] The nav menu this came from. Used only to
 *   break ties between pages that score identically.
 */
export function openNavSearch({
  query = "",
  pagesFirst = false,
  menuLabel = "",
} = {}) {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(NAV_SEARCH_OPEN_EVENT, {
      detail: { query, pagesFirst, section: resolveNavSection(menuLabel) },
    }),
  );
}
